import { Injectable } from '@angular/core';
import { DataKey, UserRole } from '@core/models';
import { languageChangedFulfilled } from '@core/modules/language/features/language.slice';
import { DataService } from '@core/services/data.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  loadUserInterests,
  loadUserInterestsFulfilled,
  loadUserInterestsRejected,
  refreshUserInterests,
  selectUser,
  selectUserInterests
} from '../features/user.slice';
import { UserInterest } from '../models';
import { UserService } from '../services/user.service';

@Injectable()
export class InterestsEffects {
  loadUserInterests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserInterests),
      concatLatestFrom(() => [
        this.store.select(selectUserInterests),
        this.store.select(selectUser)
      ]),
      filter(([, , user]) => !!user),
      switchMap(([, interests, user]) =>
        (!interests.length || this.dataService.isStaleData(DataKey.USER_INTERESTS)
          ? this.userService.getUserInterests(user?.id)
          : of(interests)
        ).pipe(
          map((interests) =>
            loadUserInterestsFulfilled({
              interests,
              categories: this.getCategories(interests)
            })
          ),
          tap(() => this.dataService.saveUpdatedDataDate(DataKey.USER_INTERESTS)),
          catchError(() => of(loadUserInterestsRejected()))
        )
      )
    )
  );

  languageChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(languageChangedFulfilled),
      map(() => refreshUserInterests())
    )
  );

  refreshUserInterests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshUserInterests),
      concatLatestFrom(() => [this.store.select(selectUser)]),
      filter(([, user]) => !!user && user?.roleId !== UserRole.SUPPORT),
      switchMap(([, user]) =>
        this.userService.getUserInterests(user?.id).pipe(
          map((interests) =>
            loadUserInterestsFulfilled({
              interests,
              categories: this.getCategories(interests)
            })
          ),
          tap(() => this.dataService.saveUpdatedDataDate(DataKey.USER_INTERESTS)),
          catchError(() => of(loadUserInterestsRejected()))
        )
      )
    )
  );

  private getCategories(interests: UserInterest[]): UserInterest[] {
    return [
      {
        name: this.translateService.instant('All'),
        id: ''
      },
      ...interests
    ];
  }

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private translateService: TranslateService,
    private store: Store,
    private dataService: DataService
  ) {}
}
